<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import Chat from '@/components/common/chat.vue'
import BModal from '@/components/common/BModal.vue'
import { commentApi, recruitmentApi } from '@/services/api'
import { useApplicationStore } from '@/config/store/applicationStore'
import { useUserStore } from '@/config/store/userStore'
import TextAreaInput from '@/components/common/textAreaInput.vue'
import CommentSVG from '@/assets/svg/screening/commentOrPost.svg'
import Editor from '@/components/common/Editor/Editor.vue'
import BButton from '@/components/common/bButton.vue'
import Comments from '@/components/common/comment/Comments.vue'

const { t } = useI18n()
const applicationStore = useApplicationStore()
const userStore = useUserStore()
const application = defineModel<{
  _id: string
  comments: {
    _id?: string
    tempId?: string
    message: string
    authorId: string
    author: {
      _id: string
      firstname: string
      lastname: string
      picture: string
    }
    taggedPopulated: {
      _id: string
      firstname: string
      lastname: string
      picture: string
    }[]
    tagged: string[]
  }[]
}>('application')

const addComment = (newComment) => {
  if (newComment.message.length < 1)
    return
  if (newComment.taggedPopulated.length > 0) {
    const toSend = newComment.taggedPopulated.map(user => ({
      user,
      comment: newComment.message,
      url: `/recruitment/interviews/${application.value._id}`,
    }))
    commentApi.sendNotifications(toSend)
  }
  addCommentToProcess(newComment)
}

const addCommentToProcess = (comment) => {
  recruitmentApi.addCommentToProcess(application.value._id, comment).promise
    .then((response) => {
      if (response.newComment) {
        application.value.comments.unshift({
          ...response.newComment,
          _created: new Date(response.newComment._created),
        })
      }
    })
}

// The process kept aside: the modal may be closed before the API answers
const deleteComment = (commentId: string) => {
  const current = application.value
  recruitmentApi.deleteCommentToProcess(current._id, commentId).promise.then(() => {
    current.comments = current.comments.filter(c => (c._id || c.tempId) !== commentId)
  })
}

const updateComment = (commentId: string, content: string) => {
  const current = application.value
  const foundComment = current.comments.find(c => (c._id || c.tempId) === commentId)
  recruitmentApi.updateCommentToProcess(current._id, commentId, {
    content,
    taggedPopulated: foundComment?.taggedPopulated,
  }).promise.catch((err) => {
    console.error('Failed to update the comment', err)
  })
}

const isOpen = computed({
  get: () => !!application.value,
  set: (value) => {
    if (!value)
      application.value = null
  },
})
</script>

<template>
  <BModal v-model="isOpen" data-cy="screening-comments-modal">
    <template #header>
      {{ t('components.common.comment.title') }}
    </template>
    <template #default v-if="application">
      <Comments
        width=""
        v-model="application.comments"
        @add-comment="addComment"
        @delete-comment="deleteComment"
        @update-comment="updateComment"
      />
    </template>
    <template #footer>
    </template>
  </BModal>
</template>
